/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { addMessages } from '../messages/index';
import PubSubUtils from '../utils/pubsub-utils';
import { publicConstants } from '../constants';

import { reissueWebToken } from './webtoken-service';
import { fetch, waitForUpdateSession, UPDATE_SESSION } from './fetch';
import { isUpdateSessionRequest, extractConfigFromError } from './utils';

const buildError = (errorItem: any, responseHeaders: any) => {
  if (responseHeaders[publicConstants.TRACE_ID]) {
    return {
      type: 'error',
      message: errorItem.localizedValue || errorItem.message,
      correlationID: responseHeaders[publicConstants.TRACE_ID]
    };
  }
  return {
    type: 'error',
    message: errorItem.localizedValue || errorItem.message
  };
};

const fetchErrorCallback = (error: any, fetchStatus: any, authInfo: any, doNotMergeMessagesForStatusCode: any) => {
  if (error.config && isUpdateSessionRequest(error.config.url, authInfo)) {
    return Promise.reject(error);
  }

  // trigger re-auth flow with first 401 request
  if (
    error.response &&
    error.response.status &&
    error.response.status === 401 &&
    !error.config.isExternalUrl &&
    !fetchStatus.getUpdateSessionStatus() &&
    error.config.fetchTimeStamp > fetchStatus.latestUpdateSessionTimeStamp
  ) {
    // unsubscribe all to UPDATE_SESSION
    PubSubUtils.unsubscribeAllWithEventType(UPDATE_SESSION);
    fetchStatus.setUpdateSessionStatus(true);
    if (authInfo?.authType === 'custom') {
      PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.EVENT_CUSTOM_REAUTH);
      const { url, config, context } = extractConfigFromError(error);
      return waitForUpdateSession(url, config, context, fetchStatus);
    }
    return reissueWebToken(error, fetchStatus, authInfo);
  }

  // handle parallel non-updatesession requests
  if (
    error.response &&
    error.response.status &&
    error.response.status === 401 &&
    !error.config.isExternalUrl &&
    fetchStatus.getUpdateSessionStatus()
  ) {
    const { url, config, context } = extractConfigFromError(error);
    return fetch(url, config, context);
  }

  if (
    error.response &&
    error.response.status &&
    error.response.status !== 422 &&
    error.response.status !== 424 &&
    !(error.response.status === 400 && error.response.data.errorClassification === 'Invalid inputs') &&
    error.config.headers.context &&
    error.response.data &&
    !doNotMergeMessagesForStatusCode
  ) {
    const errorMsgArr =
      error.response.data.errorDetails &&
      error.response.data.errorDetails.map((errorItem: any) => {
        return buildError(errorItem, error.response.headers);
      });
    const msgContext = error.config.headers.context;

    addMessages({
      messages: errorMsgArr,
      category: 'HTTP',
      context: msgContext
    });
    return Promise.reject(error);
  }
  return Promise.reject(error);
};

export default fetchErrorCallback;
